import { Injectable } from '@angular/core';
import { roundToTwo } from '@roller/booking-calculation-service';
import { hexGenerate } from '@roller/rdl/utilities/string';
import { combineLatest, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { EGiftCardDigitalMessageAction } from '@app/checkout/guest-details/gift-card-form/gift-card-digital/gift-card-digital.model';
import { IGiftCardInfoForm } from '@app/checkout/guest-details/gift-card-form/gift-card-info-form.model';
import { ProductMembershipsService } from '@app/products/product-memberships/product-memberships.service';
import {
  CBooking,
  CMembership,
  CMembershipRenewal,
  IMembershipUpgradesInfo,
  TManageOrderType
} from '@app/tickets/tickets.model';
import { AccountService } from '@core/account/account.service';
import { InitialConfigService } from '@core/app-config/initial-config.service';
import { AvailabilityCalculationService } from '@core/availability-calculation/availability-calculation.service';
import { IBookingAPI } from '@core/booking/models/booking-api.model';
import { IBookingCosts } from '@core/booking/models/booking-costs.model';
import { ICheckoutBasketObservableCollection } from '@core/booking/models/checkout-basket-observable-collection.model';
import { IOrderAPI } from '@core/booking/models/order-api.model';
import { IUserAPI } from '@core/booking/models/user-api.model';
import { EDeliveryMode } from '@core/checkout/model/delivery-mode.enum';
import { IOrderItemTicketInfo } from '@core/checkout/model/order-item-ticket-info.model';
import { IDictionary } from '@core/common.model';
import { ConsentService } from '@core/consent/consent.service';
import { DateFormatService } from '@core/date/date-format.service';
import { IDiscounts } from '@core/discounts/models/discounts.model';
import { IGiftcardCanDeduct } from '@core/gift-card/gift-card-can-deduct.model';
import { IGiftcardRedemption } from '@core/gift-card/gift-card-redemption.model';
import { LoggerService } from '@core/logger/logger.service';
import { NavigationService } from '@core/navigation/navigation.service';
import { IOrderItem, IOrderItemModifier } from '@core/order/models/order-item.model';
import { IOrder, IOrderAmounts } from '@core/order/models/order.model';
import { OrderItemService } from '@core/order/order-item.service';
import { EPaymentTypes } from '@core/payment/enums/payment-types.enum';
import { ISimpleDiscount } from '@core/payment/models/simple-discount.model';
import { IProduct } from '@core/product/models/product.model';
import { IUniqueGiftCard } from '@core/product/models/product-giftcard.model';
import { ProductService } from '@core/product/product.service';
import { RollerRecaptchaService } from '@core/roller-recaptcha/roller-recaptcha.service';
import { VenueConfigService } from '@core/venue-config/venue-config.service';
import { CPickUpTableNumber } from '@dialog/dialog-table-number/dialog-table-number.component.model';

@Injectable({
  providedIn: 'root'
})
export class OrderBookingService {
  constructor(
    private accountService: AccountService,
    private availabilityCalculationService: AvailabilityCalculationService,
    private orderItemService: OrderItemService,
    private consentService: ConsentService,
    private dateFormatService: DateFormatService,
    private initialConfigService: InitialConfigService,
    private loggerService: LoggerService,
    private navigationService: NavigationService,
    private productMembershipsService: ProductMembershipsService,
    private productService: ProductService,
    private rollerRecaptchaService: RollerRecaptchaService,
    private venueConfigService: VenueConfigService
  ) {}

  /** The /costs API does handle postage/GC but does not return individual values which we need to display as line items in CX */
  getAmountsFromBookingCosts(order: IOrder, costs: IBookingCosts): IOrderAmounts {
    const giftCard = order.giftCards.reduce((acc, card) => acc + card.amountDeducted, 0);
    const postage = this.orderItemService.getTotalPostageCost(order.items);

    return {
      ...order.amounts,
      discount: costs.discount,
      fees: costs.fees,
      feesTax: costs.feeTax,
      giftCard,
      postage,
      creditCardFees: costs.cardFee,
      subTotalTax: costs.subTotalTax,
      tax: costs.tax,
      taxExclusive: costs.taxExclusive,
      total: costs.amountOwing,
      totalIgnoringDeposit: costs.totalIgnoringDeposit,
      transactionFees: costs.transactionFee,
      deposit: costs.totalExcludingFees,
      depositTax: roundToTwo(costs.tax - costs.feeTax)
    };
  }

  /** Create DTO from order and current checkout state that can be used as payload for `/cost` and `/draft` endpoints */
  getBookingDTOFromOrder(order: IOrder): Observable<IBookingAPI> {
    const obsCollection$ = {
      availabilityToday: this.productService.getProductAvailability(new Date()),
      availableUpgrades: this.productMembershipsService.getAvailableUpgrades(),
      consent: this.consentService.getCurrentConsent()
    };
    return combineLatest(obsCollection$).pipe(
      take(1),
      map((coll: ICheckoutBasketObservableCollection) => this.mapOrderToBookingDTO(order, coll))
    );
  }

  /** Map the collection of checkout and user account items to the bookingAPI DTO.  */
  private mapOrderToBookingDTO(order: IOrder, coll: ICheckoutBasketObservableCollection): IBookingAPI {
    const rawTicketUserInfo = order.userData.giftCardUserInfo || [];
    const formResponses = order.userData.formResponse || [];
    const rawTicketHolders = order.userData.ticketHolders;
    const rawSubscriptionTerms = order.userData.subscriptionTerms;
    const editUniqueBookingId = order.existingBooking?.bookingUniqueId;
    const firstItem = order.items[0];
    const bookingApi: IBookingAPI = {
      user: this.getBookingUser(),
      editBookingUniqueId: editUniqueBookingId,
      orders: this.mapBasketItemsToIOrderAPI(
        order.items,
        rawTicketUserInfo,
        rawTicketHolders,
        coll.availabilityToday,
        coll.availableUpgrades,
        rawSubscriptionTerms
      ),
      paymentTypeId: EPaymentTypes.none, // Implies electronic transfer
      formResponses,
      payFullAmountIgnoringDeposit: order.isPayFullPriceSelected,
      discounts: this.mapDiscountResultToDiscountSimple(order.discounts),
      giftcards: this.mapGiftCardCanDeductToGiftcardRedemption(order.giftCards),
      checkoutSlug: this.initialConfigService.getInitialConfig().checkoutSlug,
      tableNumber: order.tableNumber === CPickUpTableNumber ? undefined : order.tableNumber,
      externalId: order.externalId,
      tip: order.tip,
      upgradeBookingUniqueId: this.getMembershipUniqueId(firstItem, CMembership),
      upgradePaymentToken: '',
      renewalBookingUniqueId: this.getMembershipUniqueId(firstItem, CMembershipRenewal),
      cookieConsent: this.consentService.createConsentPayload(
        coll.consent,
        this.navigationService.getCurrentURL(true) || ''
      )
    };

    if (order.partyName) {
      bookingApi.partyDetails = {
        partyName: order.partyName
      };
    }

    const recaptchaJwt = this.rollerRecaptchaService.getRecaptchaJwt();
    if (recaptchaJwt?.length > 0) {
      bookingApi.recaptchaJwt = recaptchaJwt;
    }

    return bookingApi;
  }

  /**
   * Maps the checkout items to the array to create a draft booking.
   * BookingDate is defined from the orderItem if it is a sessionPass or a pass or package with a sessionPass/pass packageItem.
   * @param basket Checkout collection of items for purchase.
   * @param giftCardCollection option - when passed will map matching order items with ticketUserInfo and single order items for the API
   * @returns Array of items ready for purchase.
   */
  mapBasketItemsToIOrderAPI(
    basket: IOrderItem[],
    rawTicketUserInfo: IGiftCardInfoForm[],
    rawTicketHolders?: IDictionary<string>,
    availabilityToday?: IProduct[],
    availableUpgrades?: IMembershipUpgradesInfo[],
    rawSubscriptionTerms?: IDictionary<boolean>
  ): IOrderAPI[] {
    // for membership upgrade/edit booking flow excludes untouched items
    basket = basket.filter((item: IOrderItem) => !this.productMembershipsService.isUnmodifiedMembership(item));
    let orderAPIList: IOrderAPI[] = [];
    const sessionBasedTypes = ['sessionpass', 'package', 'partypackage', 'recurringpass'];
    const dateTypes = ['sessionpass', 'pass', 'partypackage', 'recurringpass'];
    const uniqueRelatedBookingKeys: string[] = [
      ...new Set(basket.map((item: IOrderItem) => item.relatedOrderItemKey || ''))
    ];
    const groupedBasket: IOrderItem[][] = uniqueRelatedBookingKeys.map((relatedKey: string) =>
      basket.filter((item: IOrderItem) => item.relatedOrderItemKey === relatedKey)
    );

    if (!groupedBasket.length) {
      this.loggerService.warn('No related keys while mapping item to order API', { message: JSON.stringify(basket) });
      return [];
    }

    groupedBasket.forEach((relatedItems: IOrderItem[]) => {
      const findAnyNonAddonItem = relatedItems.find((item: IOrderItem) => !item.addonToProductId);
      if (!findAnyNonAddonItem) {
        this.loggerService.warn('Unable to find anything that is not an addon', {
          message: JSON.stringify(relatedItems)
        });
        return;
      }

      relatedItems.forEach((item: IOrderItem) => {
        const currentVenueTime = this.venueConfigService.getCurrentVenueTime();
        let bookingDateIndex: number = this.dateFormatService.toDateIndex(currentVenueTime);
        if (
          dateTypes.includes(item.productType as string) ||
          (item.productType === 'package' && (!!item.session?.date || item.bookingDate)) ||
          (item.productType === 'membership' &&
            !this.availabilityCalculationService.hasAvailability(item, availabilityToday))
        ) {
          bookingDateIndex = this.dateFormatService.toDateIndex(item.session?.date || item.bookingDate);
        } else if (item.productType === 'addon') {
          const otherItems = basket.filter((oItem: IOrderItem) => oItem.productType !== 'addon');
          const earlySession = this.orderItemService.getEarliestBookingItem(otherItems);
          if (!item.addonToProductId && earlySession) {
            bookingDateIndex = this.dateFormatService.toDateIndex(earlySession.bookingDate);
          } else {
            bookingDateIndex = this.dateFormatService.toDateIndex(item.bookingDate);
          }
        }

        if (item.productType === 'giftcard' && !!item.uniqueGiftCards?.length) {
          orderAPIList = [
            ...orderAPIList,
            ...this.mapGiftCardOrderItemsToIOrderAPI(
              item,
              rawTicketUserInfo,
              bookingDateIndex,
              findAnyNonAddonItem.uniqueId
            )
          ];
        } else {
          const orderAPIItem: IOrderAPI = this.mapOrderItemToIOrderAPI(
            item,
            bookingDateIndex,
            findAnyNonAddonItem.uniqueId
          );

          if (sessionBasedTypes.includes(item.productType as string)) {
            orderAPIItem.sessionStart = item.session
              ? this.dateFormatService.hourStringToNumber(item.session.startTime)
              : null;
            orderAPIItem.sessionEnd = item.session
              ? this.dateFormatService.hourStringToNumber(
                  item.product.appliedSessionDiscount?.endTime ?? item.session.endTime
                )
              : null;
          }

          // Attach ticket holders to orderAPIItem
          if (rawTicketHolders && item.captureTicketHolderName) {
            orderAPIItem.ticketHolderNames = this.getTicketHolderNames(rawTicketHolders, item);
          }

          if (rawSubscriptionTerms && item.productType === 'membership' && item.recurringPayment) {
            orderAPIItem.isSubscriptionTermsAccepted = rawSubscriptionTerms[item.uniqueId];
          }

          // Set upgrade booking item parts for membership upgrade scenario
          if (
            item.manageBookingType === CMembership &&
            availableUpgrades?.length &&
            item.manageMembership?.bookingItemPartId
          ) {
            orderAPIItem.upgradeBookingItemPartId = item.manageMembership.bookingItemPartId;
          }

          if (item.manageBookingType === CMembershipRenewal && item.manageMembership?.bookingItemPartId) {
            orderAPIItem.renewalBookingItemPartId = item.manageMembership.bookingItemPartId;
          }

          if (item.manageBookingType === CBooking) {
            orderAPIItem.editBookingItemId = item.existingBookingItem?.bookingItemId;
            if (item.existingBookingItem?.bookingItemIds?.length) {
              item.existingBookingItem?.bookingItemIds?.forEach((manageId: number, index: number, arr: number[]) => {
                const indexPlusOne = index + 1;
                const orderApiCopy = structuredClone(orderAPIItem);
                orderApiCopy.editBookingItemId = manageId;
                orderApiCopy.quantity = indexPlusOne <= item.quantity ? 1 : 0;
                // the last item gets the remainder
                if (indexPlusOne === arr.length && item.quantity > arr.length) {
                  orderApiCopy.quantity = orderAPIItem.quantity;
                } else {
                  orderAPIItem.quantity--;
                }

                if (index > 0) {
                  orderApiCopy.uniqueId = hexGenerate(15);
                }

                orderAPIList.push(orderApiCopy);
              });
              return;
            }
          }

          orderAPIList.push(orderAPIItem);
        }
      });
    });

    return orderAPIList;
  }

  /**
   * If we are passed a collection of IOrderItem[] gift cards that has an product matching a basket item
   * we will send each as a separate item with single quantity and ticketUserInfo to the API
   * @param item to map
   * @param giftCardCollection array of gift card order items to match against
   * @param bookingDateIndex date index for the item
   * @returns Order Item as IOrderAPI[] payload
   */
  mapGiftCardOrderItemsToIOrderAPI(
    item: IOrderItem,
    rawTicketUserInfo: IGiftCardInfoForm[],
    bookingDateIndex: number,
    relatedUniqueId: string
  ): IOrderAPI[] {
    const relatedTicketUserInfo: IGiftCardInfoForm[] = rawTicketUserInfo.filter(
      (card: IGiftCardInfoForm) =>
        card.productId === item.productId &&
        // additionally filtering custom gift cards by formId
        (!item.product.hasUserDefinedCost ||
          item.uniqueGiftCards?.find((postage: IUniqueGiftCard) => postage.formId === card.formId))
    );

    if (relatedTicketUserInfo.length) {
      const index = rawTicketUserInfo.findIndex((card: IGiftCardInfoForm) => card.productId === item.productId);

      return relatedTicketUserInfo.map((uniCard: IGiftCardInfoForm) => {
        return this.mapOrderItemToIOrderAPI(
          item,
          bookingDateIndex,
          relatedUniqueId,
          1,
          uniCard,
          index,
          rawTicketUserInfo
        );
      });
    }

    return [this.mapOrderItemToIOrderAPI(item, bookingDateIndex, relatedUniqueId)];
  }

  /**
   * Takes a IOrderItem and maps its to an API expected payload
   * @param item to map
   * @param bookingDateIndex date index  for the item - 20241023
   * @param quantity optional override
   * @param ticketUserInfo optional ticket info for the item
   * @returns Order Item as IOrderAPI payload
   */
  mapOrderItemToIOrderAPI(
    item: IOrderItem,
    bookingDateIndex: number,
    relatedUniqueId?: string,
    quantity?: number,
    uniqueForm?: IGiftCardInfoForm,
    index?: number,
    giftCardForms?: IGiftCardInfoForm[]
  ): IOrderAPI {
    const orderAPI: IOrderAPI = {
      bookingDate: bookingDateIndex.toString(),
      quantity: quantity || item.quantity,
      additionalGuests: item.additionalGuests || 0,
      productId: item.productId,
      uniqueId: item.uniqueId,
      modifiers: item.modifiers
        ? item.modifiers.map((costMod: IOrderItemModifier) => ({ id: costMod.id, quantity: costMod.quantity || 1 }))
        : []
    };

    if (item.product.hasUserDefinedCost) {
      orderAPI.product = {
        cost: item.cost
      };
    }

    if (item.comments) {
      orderAPI.comments = item.comments;
    }

    if (item.orderSelections?.length) {
      orderAPI.orderSelections = item.orderSelections;
    }

    if (item.optionSetSelections?.length) {
      orderAPI.optionSetSelections = item.optionSetSelections;
    }

    if (!!uniqueForm && !!giftCardForms) {
      orderAPI.ticketUserInfo = [this.mapGiftCardFormToOrderAPI(orderAPI, uniqueForm, index, giftCardForms)];
    }

    if (item.addonToProductId) {
      orderAPI.addonToUniqueId = relatedUniqueId;
    }

    return orderAPI;
  }

  /**
   * Map a gift card form to the OrderAPI as a ticketUserInfo array
   * @param orderAPI to map the gift card details to
   * @param uniqueForm matches the order item
   * @param index gift card index
   * @param giftCardForms all gift card forms
   * @returns ticketUserInfo instance for the API
   */
  mapGiftCardFormToOrderAPI(
    orderAPI: IOrderAPI,
    uniqueForm: IGiftCardInfoForm,
    index = 0,
    giftCardForms: IGiftCardInfoForm[]
  ) {
    const isBulkDelivery = uniqueForm?.isBulkDelivery || index === 0 ? true : false;

    let ticketUserInfo = {
      firstName: uniqueForm.firstName,
      email: uniqueForm.email,
      sendLater: uniqueForm.sendLater,
      deliveryMode: uniqueForm.deliveryMode,
      deliveryName: uniqueForm.deliveryName,
      ticketUserAddress: uniqueForm.ticketUserAddress,
      isBulkDelivery
    } as IOrderItemTicketInfo;

    if (uniqueForm.productPostage) {
      orderAPI.productPostageId = parseInt(uniqueForm.productPostage.productPostageId, 10);
      ticketUserInfo.deliveryName =
        uniqueForm.deliveryName || (isBulkDelivery ? giftCardForms[0].firstName : ticketUserInfo.firstName);
    }

    if (uniqueForm.sendLater && !!uniqueForm.sendDate) {
      ticketUserInfo = {
        ...ticketUserInfo,
        sendDate: this.dateFormatService.dateToStringDateOnlyHyphen(uniqueForm.sendDate),
        sendTime: uniqueForm.sendTime,
        sendTimeZone: uniqueForm.sendTimeZone
      };
    }

    /** Send stored message if the digital option is selected or physical gift card */
    if (
      uniqueForm.messageOptionAction === EGiftCardDigitalMessageAction.message ||
      uniqueForm.deliveryMode === EDeliveryMode.physical
    ) {
      ticketUserInfo = {
        ...ticketUserInfo,
        message: uniqueForm.message
      };
    }

    /** Send stored videoId if the option is selected */
    if (uniqueForm.messageOptionAction === EGiftCardDigitalMessageAction.video) {
      ticketUserInfo = {
        ...ticketUserInfo,
        videoId: uniqueForm.videoId
      };
    }

    /** NOTE - currently there is only ever one ticketUserInfo per Order Item */
    return ticketUserInfo;
  }

  /** Maps the more complete discount result into the simplified discount redemption. */
  mapDiscountResultToDiscountSimple(discount: IDiscounts[]): ISimpleDiscount[] {
    const result: ISimpleDiscount[] = [];
    discount.forEach((discountResult: IDiscounts) => {
      result.push({ code: discountResult.code });
    });

    return result;
  }

  /** Maps the more complete GiftCard can deduct into the simplified GiftCard redemption. */
  mapGiftCardCanDeductToGiftcardRedemption(giftCards: IGiftcardCanDeduct[]): IGiftcardRedemption[] {
    const result: IGiftcardRedemption[] = [];
    giftCards.forEach((card: IGiftcardCanDeduct) => {
      result.push({
        giftCardNumber: card.giftcardNumber,
        giftCardExpiryMonth: card.giftcardExpiryMonth,
        giftCardExpiryYear: card.giftcardExpiryYear,
        cVV: card.cVV
      });
    });

    return result;
  }

  /** Ensure the calling code is attached to the guest phone when submitting the booking */
  private getBookingUser(): IUserAPI {
    const user = this.accountService.getUserAccount();
    return {
      ...user,
      phone: (user.callingCode ?? '') + (user.phone ?? '')
    };
  }

  private getTicketHolderNames(rawTicketHolders: IDictionary<string>, bookingItem: IOrderItem): string[] {
    const ticketHoldersNames: string[] = [];

    Object.entries(rawTicketHolders)
      .filter(([key]: [string, string]) => {
        const uniqueId = key.split('-')[0];
        return bookingItem.uniqueId === uniqueId;
      })
      .forEach(([, value]: [string, string]) => {
        ticketHoldersNames.push(value);
      });

    return ticketHoldersNames;
  }

  private getMembershipUniqueId(item: IOrderItem, manageType: TManageOrderType): string | undefined {
    if (item.manageBookingType === manageType) {
      return item.manageMembership?.existingBookingUniqueId;
    }

    return undefined;
  }
}
